/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {computed, signal} from '@preact/signals-react';
import {createMutator} from '@ion/applications/src/store/SignalUtils';
import {getViewSubGroupsState, gridDefaultPageSize} from '@ion/applications/src/utils/helpers';
import {Vehicle} from '@ion/core/src/types/entities';
import {defaultGridSettings, updateGridState} from '../../store-helpers';
import {CanRequestsViewMode, Context, GridState} from '../../types';

// State signals
export const isLoading            = signal(false);
export const isInitialized        = signal(false);
export const selectedTab          = signal(Context.test);
export const preferredColumnState = signal('vehicle-manager');

export const PREFERENCE = 'vehicle-manager';

// Derived state
export const initializeVehicleManager     = isInitialized.value;
export const isVehicleManagerLoading      = computed(() => isLoading.value);
export const selectedEnvironment          = computed(() => selectedTab.value);

export const vehicleManagerPageSize = signal<number>(gridDefaultPageSize);
export const setVehicleManagerPageSize = (size) => {
  vehicleManagerPageSize.value = size;
};

//Column State
export const vehicleManagerColumnState = signal(null);
export const setVehicleManagerColumnState = (columnState) => {
  vehicleManagerColumnState.value = columnState;
};

export const vehicleManagerHasUpdate = signal(false);
export const setVehicleManagerHasUpdate = (updated: boolean) => {
  vehicleManagerHasUpdate.value = updated;
};

export const vehicleHistoryGridColumn = signal(null);
export const setVehicleHistoryGridColumn = computed(() => vehicleHistoryGridColumn.value);

export const activeVehicleTab = signal<Context>(Context.test);

export const setActiveVehicleTab = (tab: Context) => {
  activeVehicleTab.value = tab;
};

export const activeTripTab = signal('Trip1');

export const setActiveTripTab = (tab) => {
  activeTripTab.value = tab;
};

/**
 * Preserves the CAN request summary accordion expand/collapse state so the
 * details view respects user intent across request transitions.
 */
export const canRequestSummaryAccordionExpanded = signal<boolean>(true);

export const setCanRequestSummaryAccordionExpanded = (expanded: boolean): void => {
  canRequestSummaryAccordionExpanded.value = expanded;
};

/**
 * Controls which CAN Requests sub-view is shown in VehicleDetailsEditor.
 */
export const canRequestsViewMode = signal<CanRequestsViewMode>('view');
export const setCanRequestsViewMode = createMutator<CanRequestsViewMode>(canRequestsViewMode);

export const includeSubGroups = signal<boolean>(getViewSubGroupsState(PREFERENCE));

export const setIncludeSubGroups = (include: boolean): void => {
  includeSubGroups.value = include;
  setGridState({includeSubGroups: include});
};

export const managerGrid = signal<Partial<GridState>>({
  ...defaultGridSettings
});

export const setGridState = (gridState: Partial<GridState>, partial = true): Partial<GridState> => (
  updateGridState(gridState, partial, managerGrid)
);

/**
 * Defines the state for the selected vehicle.
 */
export const selectedVehicle = signal<Vehicle | null>(null);
export const setSelectedVehicle = createMutator<Vehicle | null>(selectedVehicle);

/**
 * Defines the state for updating search queries.
 */
export const vehicleManagerSearchQueries = signal(null);
export const setVehicleManagerSearchQueries = createMutator(vehicleManagerSearchQueries);
